import { z } from 'zod';

/**
 * Phase 18 v1.4 — KYC-01/02/03. Shared Zod schemas for the KYC documental
 * pipeline. Used by:
 *   - API (createZodDto via nestjs-zod) to validate `POST /admin/kyc/:id/decidir`.
 *   - api-client (responseSchema for parse) on admin queue/detail/decidir.
 *   - mobile reactive forms (admin KycDetailPage motivo input).
 *
 * Document file validation (mime, size, 3-photo composition) lives in the
 * KYC controller multipart interceptor — NOT here — because Zod cannot
 * validate a Buffer's content. The shared schema covers the JSON surface only.
 */

export const KycStatusSchema = z.enum(['pendente', 'aprovada', 'rejeitada']);
export type KycStatus = z.infer<typeof KycStatusSchema>;

export const TipoDocumentoSchema = z.enum(['rg', 'cnh']);
export type TipoDocumento = z.infer<typeof TipoDocumentoSchema>;

/**
 * Public DTO returned by:
 *   - GET /admin/kyc       — queue listing (foto*Url null; signed URLs only
 *                            issued on detail to avoid bulk-signing N×3 URLs).
 *   - GET /admin/kyc/:id   — single submissão; foto*Url populated with 1h-validity
 *                            signed GET URLs.
 *   - POST /admin/kyc/:id/decidir — echo of post-decision state.
 *
 * The bare R2 keys are NEVER surfaced to clients (T-18 — private namespace
 * defense). `fotoFrenteUrl`/`fotoVersoUrl`/`fotoSelfieUrl` carry only signed
 * pre-baked URLs from `R2Service.createPresignedGetUrl`.
 */
export const KycSubmissaoDtoSchema = z.object({
  id: z.string(),
  profissionalId: z.string(),
  tipoDocumento: TipoDocumentoSchema,
  // Populated on detail/decidir responses; null on bulk queue listings
  // (admin must click into the row to view; cheaper to defer signing).
  fotoFrenteUrl: z.string().url().nullable(),
  fotoVersoUrl: z.string().url().nullable(),
  fotoSelfieUrl: z.string().url().nullable(),
  status: KycStatusSchema,
  motivo: z.string().nullable(),
  submetidoEm: z.string(), // ISO-8601 UTC
  decididoEm: z.string().nullable(),
  // Admin display name snapshot at decision time (or null when SetNull'd
  // after the admin user was deleted). NOT the raw User.id — that is
  // internal-only.
  decididoPorEmail: z.string().nullable(),
  // Profissional nome snapshot — surfaced in admin queue so reviewer sees
  // who they are deciding on without an extra round-trip.
  profissionalNome: z.string(),
});
export type KycSubmissaoDto = z.infer<typeof KycSubmissaoDtoSchema>;

/**
 * Admin queue listing response (paginated cursor).
 */
export const KycListResponseSchema = z.object({
  items: z.array(KycSubmissaoDtoSchema),
  nextCursor: z.string().nullable(),
});
export type KycListResponse = z.infer<typeof KycListResponseSchema>;

/**
 * POST /admin/kyc/:id/decidir body. `motivo` REQUIRED when status=rejeitada
 * — server enforces with a refine; admin UI mirrors via reactive validator.
 * When status=aprovada the motivo is ignored (server drops to null).
 */
export const KycDecidirInputSchema = z
  .object({
    status: z.enum(['aprovada', 'rejeitada']),
    motivo: z.string().min(3).max(500).optional(),
  })
  .refine(
    (input) => input.status !== 'rejeitada' || (input.motivo && input.motivo.length >= 3),
    {
      message: 'motivo é obrigatório para rejeição',
      path: ['motivo'],
    },
  );
export type KycDecidirInput = z.infer<typeof KycDecidirInputSchema>;

/**
 * KYC-01 submission DTO (multipart side carries the 3 files; this schema
 * covers only the metadata field on the form). `tipoDocumento` is the sole
 * non-binary input — kept as a Zod schema for symmetry with the response DTO.
 */
export const KycSubmeterMetaSchema = z.object({
  tipoDocumento: TipoDocumentoSchema,
});
export type KycSubmeterMeta = z.infer<typeof KycSubmeterMetaSchema>;

/**
 * Phase 40 v1.9 — KYC-MOTIVO-01. Profissional self-status payload returned
 * by `GET /profissionais/me/kyc/current`. Drives the perfil KYC widget:
 *   - status='rejeitada' → renders motivo + "Reenviar documentos" CTA.
 *   - status='pendente' → renders "Aguardando revisão" card.
 *   - status='aprovada' → success card / verified badge.
 *   - status=null (no submission yet) → "Verifique sua identidade" CTA.
 *
 * Fields:
 *  - `status` carries the latest submission status (or null when the
 *    profissional has never submitted documents).
 *  - `motivo` only populated when status='rejeitada' (KYC service contract:
 *    motivo is null on aprovada/pendente).
 *  - `decididoEm` populated whenever a decision has been made (aprovada or
 *    rejeitada); null while pendente.
 *
 * `motivo` is intentionally exposed here even though the parallel push
 * payload redacts it (T-15-01) — the profissional needs the full motivo to
 * resubmit correctly, and they are the legitimate audience for their own
 * rejection reason.
 */
export const KycCurrentDtoSchema = z.object({
  status: KycStatusSchema.nullable(),
  motivo: z.string().nullable(),
  decididoEm: z.string().nullable(), // ISO-8601 UTC, null while pendente or absent
});
export type KycCurrentDto = z.infer<typeof KycCurrentDtoSchema>;
